import React, { useEffect, useState } from "react";
import PropertyContext from "../contexts/PropertyContext";
import { onSnapshot, query } from "firebase/firestore";
import { useAuth } from "../AuthContext";
import { propertyCollection, propertyDoc } from "../utils/firestorePaths";

const PropertyProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const storageKey = currentUser ? `selectedProperty_${currentUser.uid}` : null;
  const clientStorageKey = currentUser ? `selectedClient_${currentUser.uid}` : null;

  const [selectedProperty, setSelectedPropertyState] = useState(() =>
    storageKey ? localStorage.getItem(storageKey) : null
  );
  // The clientId that owns the currently-selected property. Persisted alongside
  // selectedProperty so a page reload keeps resolving nested Clients/ paths.
  const [selectedClient, setSelectedClientState] = useState(() =>
    clientStorageKey ? localStorage.getItem(clientStorageKey) : null
  );

  const setSelectedProperty = (propertyId, clientId = null) => {
    if (storageKey) {
      if (propertyId) {
        localStorage.setItem(storageKey, propertyId);
      } else {
        localStorage.removeItem(storageKey);
      }
    }
    if (clientStorageKey) {
      if (clientId) {
        localStorage.setItem(clientStorageKey, clientId);
      } else {
        localStorage.removeItem(clientStorageKey);
      }
    }
    setSelectedClientState(clientId);
    setSelectedPropertyState(propertyId);
  };
  const [status, setStatus] = useState("loading..."); // ["loading", "error", "success"]
  const [rooms, setRooms] = useState([]);
  const [itemCategories, setItemCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [propertyInformation, setPropertyInformation] = useState({});

  useEffect(() => {
    // "all-properties" (facilities portfolio view) has no single owning client;
    // per-property listeners are handled elsewhere. Require a resolved client.
    if (!selectedProperty || selectedProperty === "all-properties") return;
    if (!selectedClient) return;

    const fetchRooms = () => {
      setStatus("loading Rooms...");
      const roomsRef = propertyCollection(selectedClient, selectedProperty, "units");
      const roomsQuery = query(roomsRef);
      const unsubscribeRooms = onSnapshot(
        roomsQuery,
        (querySnapshot) => {
          const roomsArray = [];
          querySnapshot.forEach((doc) => {
            roomsArray.push({ ...doc.data(), id: doc.id });
          });
          setRooms(roomsArray);
          setStatus("success");
        },
        (error) => {
          console.error("Error loading rooms: ", error);
          setStatus("error");
        }
      );

      return unsubscribeRooms;
    };

    const fetchItemCategories = () => {
      setStatus("loading Item Categories...");
      const itemCategoriesRef = propertyCollection(
        selectedClient,
        selectedProperty,
        "itemCatagories"
      );
      const itemCategoriesQuery = query(itemCategoriesRef);
      const unsubscribeItemCategories = onSnapshot(
        itemCategoriesQuery,
        (querySnapshot) => {
          const itemCategoriesArray = [];
          querySnapshot.forEach((doc) => {
            const data = doc.data();
            itemCategoriesArray.push({
              id: doc.id,
              name: data.name || doc.id,
              displayInDownRoomsList: data.displayInDownRoomsList !== false // default true for backwards compatibility
            });
          });
          setItemCategories(itemCategoriesArray);
          setStatus("success");
        },
        (error) => {
          console.error("Error loading item categories: ", error);
          setStatus("error");
        }
      );

      return unsubscribeItemCategories;
    };

    const fetchItems = () => {
      setStatus("loading Items...");
      const itemsRef = propertyCollection(selectedClient, selectedProperty, "items");
      const itemsQuery = query(itemsRef);
      const unsubscribeItems = onSnapshot(
        itemsQuery,
        (querySnapshot) => {
          const itemsArray = [];
          querySnapshot.forEach((doc) => {
            itemsArray.push(doc.data());
          });
          setItems(itemsArray);
          setStatus("success");
        },
        (error) => {
          console.error("Error loading items: ", error);
          setStatus("error");
        }
      );

      return unsubscribeItems;
    };
    const fetchPropertyInformation = () => {
      const propertyRef = propertyDoc(selectedClient, selectedProperty);
      const unsubscribeProperty = onSnapshot(
        propertyRef,
        (doc) => {
          setStatus("success");
          setPropertyInformation(doc.data());
        },
        (error) => {
          console.error("Error loading property information: ", error);
          setStatus("error");
        }
      );

      return unsubscribeProperty;
    };

    const unsubscribeRooms = fetchRooms();
    const unsubscribeItemCategories = fetchItemCategories();
    const unsubscribeItems = fetchItems();
    const unsubscribeProperty = fetchPropertyInformation();

    setStatus("complete");
    return () => {
      unsubscribeRooms();
      unsubscribeItemCategories();
      unsubscribeItems();
      unsubscribeProperty();
    };
  }, [selectedProperty, selectedClient]);

  return (
    <PropertyContext.Provider
      value={{
        selectedProperty,
        selectedClient,
        setSelectedProperty,
        status,
        propertyInformation,
        rooms,
        itemCategories,
        items,
      }}
    >
      {children}
    </PropertyContext.Provider>
  );
};

export default PropertyProvider;
